import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, ArrowRight, Globe, Palette, Layout, Smartphone, Search, Zap } from 'lucide-react'
import { AppSignupLink } from '@/components/AppSignupLink'
import { capability, managedService } from '@gjl/voice/pricing'

const websiteCapability = capability('website')!
const managedWebsite = managedService('managed-website')

const features = [
  {
    icon: Layout,
    title: 'Multi-Page Websites',
    description:
      'Create as many pages as you need: Home, About, Events, Contact, and custom pages for programs, artists, or seasons.',
  },
  {
    icon: Palette,
    title: 'Beautiful Templates',
    description:
      'Start with professionally designed templates built for music organizations. Customize colors, fonts, and layouts.',
  },
  {
    icon: Smartphone,
    title: 'Mobile-First Design',
    description:
      'Every page looks great on phones and tablets. Your audience browses on mobile, your site should work there.',
  },
  {
    icon: Globe,
    title: 'Custom Domains',
    description:
      'Use your own domain (yourorchestra.org) with free SSL certificates. Or use a free fuguelab.app subdomain.',
  },
  {
    icon: Search,
    title: 'SEO Built-In',
    description:
      'Add meta titles, descriptions, and keywords. Your pages are optimized for Google search from day one.',
  },
  {
    icon: Zap,
    title: 'Fast & Reliable',
    description:
      'Built on modern infrastructure that loads instantly. No slow page loads or downtime during ticket rushes.',
  },
]

const comparisons = [
  { feature: 'Multi-page websites', fugue: true, squarespace: true, wordpress: true },
  { feature: 'Built for music orgs', fugue: true, squarespace: false, wordpress: false },
  { feature: 'Event management included', fugue: true, squarespace: false, wordpress: false },
  { feature: 'Email newsletters included', fugue: true, squarespace: false, wordpress: false },
  { feature: 'Donations included', fugue: true, squarespace: false, wordpress: false },
  { feature: 'No coding required', fugue: true, squarespace: true, wordpress: false },
  { feature: 'Flat monthly pricing', fugue: true, squarespace: false, wordpress: false },
]

const otherFeatures = [
  { title: 'Ticketing', href: '/platform/features/events', description: 'Event pages with RSVP' },
  { title: 'Email', href: '/platform/features/email', description: 'Send newsletters' },
  { title: 'Donor Management', href: '/platform/features/donations', description: 'Accept support' },
  { title: 'Analytics', href: '/platform/features/analytics', description: 'Track growth' },
  { title: 'Applications', href: '/platform/features/applications', description: 'Run auditions' },
]

export default function WebsiteFeaturePage() {
  useSEO({
    title: 'Website Builder for Music Organizations | Fugue Lab App',
    description:
      'Build and edit your pages in the Fugue Lab app: concerts, artists and programs updated by your own team, on your own domain, without touching code.',
    url: '/platform/features/website',
    canonical: '/platform/features/website',
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const stepsRef = useScrollReveal<HTMLDivElement>('.step-item', { stagger: 0.15 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.15 })
  const otherFeaturesRef = useStaggeredGrid<HTMLDivElement>('.other-feature-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-4 py-20">
        <div ref={heroRef} className="container mx-auto max-w-4xl text-center">
          <div className="bg-primary/10 text-primary mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
            <Globe className="h-4 w-4" />
            Website Builder
          </div>
          <h1 className="mb-6 text-4xl font-bold md:text-5xl">
            A professional website
            <br />
            without the hassle
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Build a beautiful, mobile-responsive website for your music organization. No coding required. Create pages,
            upload photos, and launch in minutes, not months.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <AppSignupLink>
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Start Building <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </m.div>
            </AppSignupLink>
            <Link to="/platform">
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 text-lg">
                  See All Features
                </Button>
              </m.div>
            </Link>
          </div>
          <p className="text-muted-foreground mt-4 text-sm">
            ${websiteCapability.priceMonthly}/month, month to month. No contracts.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-surface px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Everything You Need</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              A complete website builder designed specifically for music organizations.
            </p>
          </div>
          <div ref={featuresRef} className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <m.div
                key={feature.title}
                className="feature-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <feature.icon className="text-primary mb-4 h-10 w-10" />
                <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-4 py-20">
        <div ref={stepsRef} className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Launch Your Website in 3 Steps</h2>
          </div>
          <div className="space-y-8">
            {[
              {
                step: '1',
                title: 'Choose a template',
                description:
                  'Pick from professionally designed templates built for music organizations. Each template includes suggested pages and sections.',
              },
              {
                step: '2',
                title: 'Add your content',
                description:
                  'Upload photos, write your story, and customize colors to match your brand. The drag-and-drop editor makes it easy.',
              },
              {
                step: '3',
                title: 'Publish & share',
                description:
                  'Click publish and your site is live. Share it on social media, add it to your email signature, and watch your audience grow.',
              },
            ].map((item) => (
              <div key={item.step} className="step-item flex gap-6">
                <m.div
                  className="bg-primary text-primary-foreground flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-xl font-bold"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {item.step}
                </m.div>
                <div>
                  <h3 className="mb-2 text-xl font-semibold">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="bg-surface px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Why Fugue Lab?</h2>
            <p className="text-muted-foreground">Built specifically for music organizations, not generic websites.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-border border-b">
                  <th className="px-4 py-4 text-left font-semibold">Feature</th>
                  <th className="text-primary px-4 py-4 text-center font-semibold">Fugue Lab</th>
                  <th className="text-muted-foreground px-4 py-4 text-center font-semibold">Squarespace</th>
                  <th className="text-muted-foreground px-4 py-4 text-center font-semibold">WordPress</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row) => (
                  <tr key={row.feature} className="border-border border-b">
                    <td className="px-4 py-4">{row.feature}</td>
                    <td className="px-4 py-4 text-center">
                      {row.fugue ? (
                        <Check className="mx-auto h-5 w-5 text-green-500" />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {row.squarespace ? (
                        <Check className="text-muted-foreground mx-auto h-5 w-5" />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {row.wordpress ? (
                        <Check className="text-muted-foreground mx-auto h-5 w-5" />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Simple Pricing</h2>
          </div>
          <div ref={pricingRef} className="mx-auto grid max-w-3xl gap-8 md:grid-cols-2">
            <m.div
              className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">Self-serve, available today</div>
              <h3 className="mb-2 text-2xl font-bold">{websiteCapability.name}</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${websiteCapability.priceMonthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Hosting with SSL included</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Editor you run yourself</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Custom domain</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Forms and SEO built in</span>
                </li>
              </ul>
              <Link to="/get-started" className="block">
                <m.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button className="w-full cursor-pointer">Get Started</Button>
                </m.div>
              </Link>
            </m.div>
            <m.div
              className="pricing-card border-border bg-card rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">Done for you</div>
              <h3 className="mb-2 text-2xl font-bold">{managedWebsite.name}</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${managedWebsite.priceMonthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Website capability included</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Unlimited update requests</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">One active request at a time</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">72-hour turnaround</span>
                </li>
              </ul>
              <Link to="/services/done-for-you#managed-website" className="block">
                <m.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button variant="outline" className="w-full cursor-pointer">
                    See Managed Website
                  </Button>
                </m.div>
              </Link>
            </m.div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/pricing" className="text-primary font-medium hover:underline">
              See pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="bg-surface px-4 py-16">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold">More Features</h2>
            <p className="text-muted-foreground">Website builder is just the beginning.</p>
          </div>
          <div ref={otherFeaturesRef} className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {otherFeatures.map((feature) => (
              <m.div
                key={feature.href}
                className="other-feature-card"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Link
                  to={feature.href}
                  className="border-border bg-card hover:border-primary block h-full rounded-lg border p-4 text-center transition-colors"
                >
                  <h3 className="font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Link>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 px-4 py-16">
        <div ref={ctaRef} className="container mx-auto max-w-3xl text-center">
          <div className="cta-content">
            <h2 className="mb-4 text-3xl font-bold">Ready to build your website?</h2>
            <p className="text-muted-foreground mb-8 text-xl">
              Join music organizations using Fugue Lab to grow their online presence.
            </p>
            <AppSignupLink>
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-block"
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Start Building <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </m.div>
            </AppSignupLink>
          </div>
        </div>
      </section>
    </div>
  )
}
